import { normalizeTransportAddressList } from '../../utils/transportAddressUtils';
export { normalizeTransportAddressList } from '../../utils/transportAddressUtils';

export function buildTransportAddressFields(source: Record<string, unknown>): {
  loadingAddresses: string[];
  deliveryAddresses: string[];
} {
  const loadingAddresses = normalizeTransportAddressList(source.loadingAddresses);
  const deliveryAddresses = normalizeTransportAddressList(source.deliveryAddresses);

  return {
    loadingAddresses,
    deliveryAddresses,
  };
}

function stripTechnicalRoutePrefix(value: string): string {
  return value
    .replace(/^[A-Z]{2}-[A-Z0-9]+-\d{12,}\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function compactEndpointLabel(value: string): string {
  return stripTechnicalRoutePrefix(value)
    .replace(/\bLoading Dock\b/i, '')
    .replace(/\bDelivery Gate\b/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isLowSignalEndpointLabel(value: string): boolean {
  return /^(UI\s+)?Loading\b/i.test(value) || /^(UI\s+)?Delivery\b/i.test(value);
}

export function extractTransportRouteEndpoint(address: string | undefined): string {
  if (!address) return '';
  const lines = address
    .split(/\n|;/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    const postalMatch = line.match(/\b\d{2}-\d{3}\s+(.+?)(?:,|$)/);
    if (postalMatch) return postalMatch[1].trim();
  }

  const addressParts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const countryNames = new Set(['polska', 'poland', 'pl']);
  const candidateParts = addressParts.filter((part) => !countryNames.has(part.toLowerCase()));
  const cityCandidate = candidateParts[candidateParts.length - 1];

  if (cityCandidate && !/^\d/.test(cityCandidate) && cityCandidate.length < 48) {
    return compactEndpointLabel(cityCandidate);
  }

  const firstLine = compactEndpointLabel(lines[0] || '');
  return firstLine.slice(0, 40);
}

export function buildTransportRouteLabel(
  loadingAddresses: string[],
  deliveryAddresses: string[],
  fallbackOrigin?: string | null,
  fallbackDestination?: string | null,
): { label: string; origin: string; destination: string; tooltip: string } {
  const addressOrigin = extractTransportRouteEndpoint(loadingAddresses[0]);
  const addressDestination = extractTransportRouteEndpoint(deliveryAddresses[0]);
  const fallbackOriginLabel = extractTransportRouteEndpoint(fallbackOrigin || undefined);
  const fallbackDestinationLabel = extractTransportRouteEndpoint(fallbackDestination || undefined);
  const origin = addressOrigin && !isLowSignalEndpointLabel(addressOrigin)
    ? addressOrigin
    : fallbackOriginLabel || addressOrigin;
  const destination = addressDestination && !isLowSignalEndpointLabel(addressDestination)
    ? addressDestination
    : fallbackDestinationLabel || addressDestination;

  if (!origin && !destination) {
    return { label: '-', origin: '-', destination: '-', tooltip: '-' };
  }

  const resolvedOrigin = origin || '?';
  const resolvedDestination = destination || '?';
  const label = `${resolvedOrigin} → ${resolvedDestination}`;
  const tooltipParts: string[] = [];
  if (loadingAddresses.length > 0) tooltipParts.push(loadingAddresses.join('\n\n'));
  if (deliveryAddresses.length > 0) tooltipParts.push(deliveryAddresses.join('\n\n'));

  return {
    label,
    origin: resolvedOrigin,
    destination: resolvedDestination,
    tooltip: tooltipParts.join('\n↓\n') || label,
  };
}
